import { useState, useEffect } from 'react'
import { MessageSquare, Bug, Lightbulb, Sparkles, HelpCircle, Clock, CheckCircle, Archive, Eye, RefreshCw } from 'lucide-react'
import { supabase, isSupabaseConfigured } from '../../utils/supabase'

const categoryIcons = {
    bug: Bug,
    feature: Lightbulb,
    improvement: Sparkles,
    other: HelpCircle,
}

const categoryColors = {
    bug: 'text-red-400 bg-red-500/20',
    feature: 'text-yellow-400 bg-yellow-500/20',
    improvement: 'text-primary-400 bg-primary-500/20',
    other: 'text-white/60 bg-white/10',
}

const statusColors = {
    new: 'bg-blue-500/20 text-blue-400',
    reviewed: 'bg-yellow-500/20 text-yellow-400',
    resolved: 'bg-green-500/20 text-green-400',
    archived: 'bg-white/10 text-white/40',
}

export default function FeedbackTable() {
    const [feedback, setFeedback] = useState([])
    const [loading, setLoading] = useState(true)
    const [filter, setFilter] = useState('all') // all, new, reviewed, resolved
    const [categoryFilter, setCategoryFilter] = useState('all')
    const [selectedFeedback, setSelectedFeedback] = useState(null)

    useEffect(() => {
        fetchFeedback()
    }, [])

    const fetchFeedback = async () => {
        if (!isSupabaseConfigured()) {
            setLoading(false)
            return
        }

        try {
            const { data, error } = await supabase
                .from('feedback')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(100)

            if (error) throw error
            setFeedback(data || [])
        } catch (error) {
            console.error('Error fetching feedback:', error)
        } finally {
            setLoading(false)
        }
    }

    const updateStatus = async (id, newStatus) => {
        try {
            const { error } = await supabase
                .from('feedback')
                .update({
                    status: newStatus,
                    reviewed_at: newStatus !== 'new' ? new Date().toISOString() : null
                })
                .eq('id', id)

            if (error) throw error

            setFeedback(prev => prev.map(f =>
                f.id === id ? { ...f, status: newStatus } : f
            ))
            setSelectedFeedback(null)
        } catch (error) {
            console.error('Error updating status:', error)
        }
    }

    const filteredFeedback = feedback.filter(f => {
        if (filter !== 'all' && f.status !== filter) return false
        if (categoryFilter !== 'all' && f.category !== categoryFilter) return false
        return true
    })

    const stats = {
        total: feedback.length,
        new: feedback.filter(f => f.status === 'new').length,
        reviewed: feedback.filter(f => f.status === 'reviewed').length,
        resolved: feedback.filter(f => f.status === 'resolved').length,
    }

    const formatTime = (timestamp) => {
        const date = new Date(timestamp)
        const now = new Date()
        const diff = now - date

        if (diff < 60000) return 'Just now'
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
        return date.toLocaleDateString()
    }

    return (
        <div className="glass-card p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-white">User Feedback</h2>
                        <p className="text-sm text-white/50">{stats.total} total submissions</p>
                    </div>
                </div>
                <button
                    onClick={fetchFeedback}
                    className="btn-ghost text-sm"
                >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-3 mb-6">
                <button
                    onClick={() => setFilter('all')}
                    className={`p-3 rounded-lg text-center transition-colors ${filter === 'all' ? 'bg-white/10' : 'bg-white/5 hover:bg-white/10'}`}
                >
                    <p className="text-xl font-bold text-white">{stats.total}</p>
                    <p className="text-xs text-white/50">Total</p>
                </button>
                <button
                    onClick={() => setFilter('new')}
                    className={`p-3 rounded-lg text-center transition-colors ${filter === 'new' ? 'bg-blue-500/20' : 'bg-white/5 hover:bg-white/10'}`}
                >
                    <p className="text-xl font-bold text-blue-400">{stats.new}</p>
                    <p className="text-xs text-white/50">New</p>
                </button>
                <button
                    onClick={() => setFilter('reviewed')}
                    className={`p-3 rounded-lg text-center transition-colors ${filter === 'reviewed' ? 'bg-yellow-500/20' : 'bg-white/5 hover:bg-white/10'}`}
                >
                    <p className="text-xl font-bold text-yellow-400">{stats.reviewed}</p>
                    <p className="text-xs text-white/50">Reviewed</p>
                </button>
                <button
                    onClick={() => setFilter('resolved')}
                    className={`p-3 rounded-lg text-center transition-colors ${filter === 'resolved' ? 'bg-green-500/20' : 'bg-white/5 hover:bg-white/10'}`}
                >
                    <p className="text-xl font-bold text-green-400">{stats.resolved}</p>
                    <p className="text-xs text-white/50">Resolved</p>
                </button>
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 mb-4 flex-wrap">
                {['all', 'bug', 'feature', 'improvement', 'other'].map(cat => (
                    <button
                        key={cat}
                        onClick={() => setCategoryFilter(cat)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${categoryFilter === cat
                                ? 'bg-primary-500/20 text-primary-400'
                                : 'bg-white/5 text-white/60 hover:bg-white/10'
                            }`}
                    >
                        {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </button>
                ))}
            </div>

            {/* Feedback List */}
            {loading ? (
                <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-white/50 text-sm">Loading feedback...</p>
                </div>
            ) : filteredFeedback.length === 0 ? (
                <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/50">No feedback found</p>
                </div>
            ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto custom-scrollbar">
                    {filteredFeedback.map((item) => {
                        const CategoryIcon = categoryIcons[item.category] || HelpCircle
                        return (
                            <div
                                key={item.id}
                                className="p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                                onClick={() => setSelectedFeedback(item)}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3 flex-grow min-w-0">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${categoryColors[item.category]}`}>
                                            <CategoryIcon className="w-4 h-4" />
                                        </div>
                                        <div className="min-w-0 flex-grow">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`px-2 py-0.5 rounded text-xs ${statusColors[item.status]}`}>
                                                    {item.status}
                                                </span>
                                                <span className="text-xs text-white/40">{item.category}</span>
                                            </div>
                                            <p className="text-white text-sm line-clamp-2">{item.message}</p>
                                            <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                                                <span>{item.email || 'Anonymous'}</span>
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {formatTime(item.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <button className="text-white/40 hover:text-white p-1">
                                        <Eye className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Detail Modal */}
            {selectedFeedback && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                        onClick={() => setSelectedFeedback(null)}
                    />
                    <div className="relative w-full max-w-lg glass-card p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryColors[selectedFeedback.category]}`}>
                                {(() => {
                                    const Icon = categoryIcons[selectedFeedback.category] || HelpCircle
                                    return <Icon className="w-5 h-5" />
                                })()}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white capitalize">{selectedFeedback.category}</h3>
                                <p className="text-sm text-white/50">{selectedFeedback.email || 'Anonymous'}</p>
                            </div>
                        </div>

                        <div className="p-4 rounded-lg bg-white/5 mb-4">
                            <p className="text-white whitespace-pre-wrap">{selectedFeedback.message}</p>
                        </div>

                        <div className="text-xs text-white/40 space-y-1 mb-4">
                            <p>Page: {selectedFeedback.page_url || 'Unknown'}</p>
                            <p>Submitted: {new Date(selectedFeedback.created_at).toLocaleString()}</p>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => updateStatus(selectedFeedback.id, 'reviewed')}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-colors"
                            >
                                <Eye className="w-4 h-4" />
                                Mark Reviewed
                            </button>
                            <button
                                onClick={() => updateStatus(selectedFeedback.id, 'resolved')}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
                            >
                                <CheckCircle className="w-4 h-4" />
                                Resolved
                            </button>
                            <button
                                onClick={() => updateStatus(selectedFeedback.id, 'archived')}
                                className="px-4 py-2 rounded-lg bg-white/10 text-white/60 hover:bg-white/20 transition-colors"
                            >
                                <Archive className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
